import { useSelector } from 'react-redux';
import React, { useMemo } from 'react';
import { ACCOUNT_TYPES } from '../../../../app/appConstants';
import _ from 'lodash';
import BinIcon from '../../../../components/icon/Icon';
import {
    LiteTradeSelectAccountInfoDescriptionStyled,
    LiteTradeSelectAccountInfoStyled,
    LiteTradeSelectAccountStyled,
    LiteTradeSelectAccountTitleStyled,
} from './styled/LiteTradeSelectAccountStyled';
import { Slider, TSliderToggleComponent } from '../../../../components/Slider/Slider';
import { getLiteTradeAllAccountsMetrics } from '../../liteTradeSelectors';
import { useLangContext } from '../../../../providers/LangProvider/LangProviderContext';
import LiteTradeSelectAccountRow from './LiteTradeSelectAccountRow';
import type { TMetricParam } from '../../../../app/types/appReducer.t';
import useSelectAccount from './hooks/useSelectAccount';
import amountFormat from "../../../../utils/amountFormat";
import LiteTradeSelectAccountToggle from "./LiteTradeSelectAccountToggle";
import type { IAccountType } from "../../../../app/types/appActions.t";

interface LiteTradeSelectAccountProps {
    backdropClassName?: string,
    metricParam?: TMetricParam,
    onSelectAccountClick?: () => void,
    onSelectAccountClose?: ({ newValue, oldValue, source }: {
        newValue: string,
        oldValue: string,
        source?: string
    }) => void,
    popUpClassName?: string,
    showCurrentValue?: boolean,
    getToggleComponent?: (
        onClick: (event: any) => void,
        selectedAccount?: { accountCode: string, type: IAccountType },
        disabled?: boolean
    ) => TSliderToggleComponent,
}

function LiteTradeSelectAccount(props: LiteTradeSelectAccountProps) {
    const {
        backdropClassName,
        metricParam = 'equity',
        onSelectAccountClick,
        onSelectAccountClose,
        popUpClassName,
        showCurrentValue,
        getToggleComponent,
    } = props;
    const { getLangPath } = useLangContext();
    const metrics = useSelector(getLiteTradeAllAccountsMetrics);
    const {
        accounts,
        handleAccountSelect,
        handleSelectAccountCancel,
        selectedAccount,
        handleSelectAccountToggleClick,
        disabled,
    } = useSelectAccount({ onSelectAccountClose, onSelectAccountClick });

    const toggleComponent = useMemo(() => {
        if (!selectedAccount) return null;
        if (typeof getToggleComponent !== 'function') {
            return <LiteTradeSelectAccountToggle
                onClick={handleSelectAccountToggleClick}
                showCurrentValue={showCurrentValue}
                type={selectedAccount.type}
                value={amountFormat(metrics[selectedAccount.accountCode][metricParam], 2, '$')}
                disabled={disabled}
            />
        }
        return getToggleComponent(handleSelectAccountToggleClick, selectedAccount, disabled);
    }, [disabled, getToggleComponent, handleSelectAccountToggleClick, metricParam, metrics, selectedAccount, showCurrentValue]);

    if (!selectedAccount || !metrics?.[selectedAccount.accountCode]) {
        return <LiteTradeSelectAccountStyled className='wave-background min-width'/>;
    }

    return <LiteTradeSelectAccountStyled>
        <Slider
            showCloseButton={false}
            popUpClassName={popUpClassName}
            backdropClassName={backdropClassName}
            onClose={handleSelectAccountCancel}
            toggleComponent={toggleComponent}
            placement={'bottom'}>
            <LiteTradeSelectAccountTitleStyled>{getLangPath('lite_version.home.accounts')}</LiteTradeSelectAccountTitleStyled>
            <>
                {accounts.map(account => <LiteTradeSelectAccountRow
                    account={account}
                    handleAccountSelect={handleAccountSelect}
                    key={account.name}
                    metricParam={metricParam}
                />)}
            </>
            <LiteTradeSelectAccountInfoStyled>
                <div className='d-flex align-items-center me-2'>
                    <BinIcon name={'IcInfo'}/>
                </div>
                <LiteTradeSelectAccountInfoDescriptionStyled>
                    {getLangPath(`lite_version.home.switch_to_${_.toLower(selectedAccount.type) === ACCOUNT_TYPES.DEMO ? 'real' : 'demo'}`)}
                </LiteTradeSelectAccountInfoDescriptionStyled>
            </LiteTradeSelectAccountInfoStyled>
        </Slider>
    </LiteTradeSelectAccountStyled>;
}

export default LiteTradeSelectAccount;
